import { NavLink, Outlet, Link, useLocation } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { Home, LayoutGrid, ReceiptText, ShoppingBag, UserRound } from "lucide-react";
import { cn } from "@tarawa/ui";
import { useCart } from "@/lib/cart";
import { useI18n, type TKey } from "@/lib/i18n";
import { Logo } from "./Logo";
import { LanguageSwitch } from "./LanguageSwitch";
import { useGoogleReturn } from "./GoogleAuth";
import { CartBar, isShoppingPath } from "./CartBar";

const NAV: { to: string; label: TKey; icon: typeof Home; end?: boolean }[] = [
  { to: "/", label: "nav.home", icon: Home, end: true },
  { to: "/categories", label: "nav.categories", icon: LayoutGrid },
  { to: "/orders", label: "nav.orders", icon: ReceiptText },
  { to: "/cart", label: "nav.cart", icon: ShoppingBag },
  { to: "/account", label: "nav.account", icon: UserRound },
];

function CartBadge({ className }: { className?: string }) {
  const { count } = useCart();
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.span
          key={count}
          initial={{ scale: 0.6 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 22 }}
          className={cn(
            "ltr-nums absolute flex h-5 min-w-5 items-center justify-center rounded-full bg-saffron-400 px-1 text-[11px] font-bold text-ink",
            className,
          )}
        >
          {count > 99 ? "99+" : count}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

/** Mobile: bottom navigation bar. Tablet/desktop: top header with the same items. */
export function Layout() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const hideBottomNav = pathname.startsWith("/checkout");
  const { count } = useCart();
  // Room for the mobile cart bar so it never covers the end of the page.
  const cartBarShown = !hideBottomNav && count > 0 && isShoppingPath(pathname);
  useGoogleReturn();

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-40 hidden border-b border-line/70 bg-surface/90 backdrop-blur md:block">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-6">
          <Link to="/" aria-label={t("nav.home")}>
            <Logo />
          </Link>
          <nav className="flex flex-1 items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "relative flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition",
                    isActive ? "bg-brand-50 text-brand-700" : "text-muted hover:text-ink",
                  )
                }
              >
                <span className="relative">
                  <Icon className="size-[18px]" />
                  {to === "/cart" && <CartBadge className="-top-2 -end-3" />}
                </span>
                {t(label)}
              </NavLink>
            ))}
          </nav>
          <LanguageSwitch />
        </div>
      </header>

      <main className={cn("mx-auto max-w-6xl px-4 pt-4 md:px-6 md:pt-8", hideBottomNav ? "pb-8" : cartBarShown ? "pb-44 md:pb-12" : "pb-28 md:pb-12")}>
        <Outlet />
      </main>

      {!hideBottomNav && <CartBar />}

      {!hideBottomNav && (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-line/70 bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
          aria-label="Navigation"
        >
          <ul className="grid grid-cols-5">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors",
                      isActive ? "text-brand-700" : "text-subtle",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          "relative flex h-8 w-14 items-center justify-center rounded-full transition-colors",
                          isActive && "bg-brand-50",
                        )}
                      >
                        <Icon className="size-[22px]" strokeWidth={isActive ? 2.4 : 2} />
                        {to === "/cart" && <CartBadge className="-top-1 end-1.5" />}
                      </span>
                      {t(label)}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
